import React, { useEffect, useRef, useState, useCallback } from "react";
import { createLabScene, DEFAULT_PARAMS } from "./labScene";
import DebugPanel from "./DebugPanel";

/**
 * Frame 001 · Laboratory
 *
 * A research environment, not a product. Route: /lab/frame-001
 * Governed by:
 *   /app/memory/FRAME_001_SPECIFICATION.md
 *   /app/memory/MATERIAL_SPECIFICATION.md
 *
 * The scene contains a frosted white surface, darkness behind it,
 * and one distant colored source of light. Everything else is
 * observation apparatus.
 */

const PRESET_STORAGE_KEY = "frame001_lab_presets_v1";

export default function Frame001Lab() {
  const containerRef = useRef(null);
  const sceneApiRef = useRef(null);

  const [params, setParams] = useState({ ...DEFAULT_PARAMS });
  const [uiHidden, setUiHidden] = useState(false);
  const [presets, setPresets] = useState({});
  const [currentPresetName, setCurrentPresetName] = useState("");

  // Load presets from localStorage on mount.
  useEffect(() => {
    try {
      const raw = localStorage.getItem(PRESET_STORAGE_KEY);
      if (raw) setPresets(JSON.parse(raw));
    } catch {
      // ignore malformed presets
    }
  }, []);

  // Mount the Three.js scene.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const api = createLabScene({
      container,
      initialParams: params,
    });
    sceneApiRef.current = api;

    return () => {
      api.dispose();
      sceneApiRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Push parameter changes into the scene without triggering React re-mounts.
  useEffect(() => {
    if (sceneApiRef.current) {
      sceneApiRef.current.setParams(params);
    }
  }, [params]);

  // Keyboard: H to toggle UI.
  useEffect(() => {
    const onKey = (e) => {
      // Ignore if user is typing in an input (preset name, etc.)
      const tag = (e.target && e.target.tagName) || "";
      if (tag === "INPUT" || tag === "SELECT" || tag === "TEXTAREA") return;
      if (e.key === "h" || e.key === "H") {
        setUiHidden((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const onParamChange = useCallback((key, value) => {
    setParams((prev) => ({ ...prev, [key]: value }));
  }, []);

  const resetScene = useCallback(() => {
    setParams({ ...DEFAULT_PARAMS });
    setCurrentPresetName("");
  }, []);

  const takeScreenshot = useCallback(
    (name) => {
      if (sceneApiRef.current) {
        sceneApiRef.current.screenshot({ presetName: name || "unsaved" });
      }
    },
    [],
  );

  const savePreset = useCallback(
    (name) => {
      setPresets((prev) => {
        const next = {
          ...prev,
          [name]: {
            ...params,
            savedAt: new Date().toISOString(),
          },
        };
        try {
          localStorage.setItem(PRESET_STORAGE_KEY, JSON.stringify(next));
        } catch {
          // localStorage might be full or disabled
        }
        return next;
      });
      setCurrentPresetName(name);
    },
    [params],
  );

  const loadPreset = useCallback(
    (name) => {
      const p = presets[name];
      if (!p) return;
      const { savedAt: _savedAt, ...restored } = p;
      // Merge into defaults so any newly added param has a value.
      setParams({ ...DEFAULT_PARAMS, ...restored });
      setCurrentPresetName(name);
    },
    [presets],
  );

  return (
    <div
      data-testid="frame001-lab"
      style={{
        position: "fixed",
        inset: 0,
        background: "#000",
        overflow: "hidden",
      }}
    >
      <div
        ref={containerRef}
        data-testid="frame001-lab-canvas"
        style={{
          position: "absolute",
          inset: 0,
        }}
      />

      {uiHidden && (
        <button
          onClick={() => setUiHidden(false)}
          data-testid="btn-show-ui"
          style={{
            position: "fixed",
            top: 16,
            right: 16,
            background: "rgba(0,0,0,0.35)",
            border: "1px solid rgba(255,255,255,0.12)",
            color: "rgba(255,255,255,0.5)",
            fontFamily: "ui-monospace, SFMono-Regular, monospace",
            fontSize: 10,
            padding: "4px 10px",
            borderRadius: 3,
            cursor: "pointer",
            letterSpacing: "0.12em",
            zIndex: 10,
          }}
          title="Show UI (H)"
        >
          show (H)
        </button>
      )}

      {!uiHidden && (
        <DebugPanel
          params={params}
          onParamChange={onParamChange}
          onReset={resetScene}
          onScreenshot={takeScreenshot}
          onSavePreset={savePreset}
          onLoadPreset={loadPreset}
          onHideUI={() => setUiHidden(true)}
          presets={presets}
          currentPresetName={currentPresetName}
        />
      )}
    </div>
  );
}
